import throttle from 'lodash/throttle';
import React, { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Linking,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

import {
  PinCodeDispatchMethod,
  Platform as ReCaptchaPlatform,
} from 'apollo/queries/autogenerate/schemas-public';
import useInitiateAuth from 'shared/modules/auth/hooks/use-initiate-auth';
import Button from 'shared/modules/common/components/atoms/Button';
import Icon from 'shared/modules/common/components/atoms/Icon';
import TextInput from 'shared/modules/common/components/atoms/TextInput';
import validator from 'shared/utils/validator';

const TERMS_URL = 'https://terms.paidy.com/personal-data';
const FAQ_URL =
  'https://cs-support.paidy.com/support/solutions/folders/150000270430';

enum CustomErrorCode {
  AUTH_FAIL,
  OTP_FAIL,
  WAF_FAIL,
}

type SignInForm = {
  email: string;
  phone: string;
};

type SessionCredentials = SignInForm & {
  sessionToken: string;
};

type Props = {
  /**
   * Invoked when a field is focused
   */
  onFocus?: (fieldName: string) => void;
  /**
   * Invoked when the Submit button is pressed
   */
  onSubmit?: () => void;
  /**
   * Invoked an error occurred
   */
  onError?: (error?: Error) => void;
  /**
   * Invoked when credentials are valid
   */
  onSuccess?: (props: SessionCredentials) => void;
  /**
   * AUTH_FAIL | OTP_FAIL | WAF_FAIL
   */
  customErrorCode?: CustomErrorCode;
  /**
   * WEB | IOS | ANDROID
   */
  reCaptchaPlatform: ReCaptchaPlatform;
  /**
   *  Every signin attempt requires a new ReCaptcha token. This function should return a Promise with the ReCaptcha token as the resolved value.
   */
  getRecaptchaTokenFunction: () => Promise<string>;
};

const SignIn = (props: Props) => {
  const form = useSignInForm();

  const initiateAuth = useInitiateAuth();

  const validateThenSubmit = async () => {
    props.onSubmit?.();

    const isEmailValid = await form.triggerValidation('email');
    const isPhoneValid = await form.triggerValidation('phone');

    // mustNotBeMisspelledDomain errors to be ignored and not to be used to block users
    if (
      (isEmailValid ||
        form?.errors?.email?.type === 'mustNotBeMisspelledDomain') &&
      isPhoneValid
    ) {
      submit();
    }
  };

  const submit = useCallback(
    throttle(
      async () => {
        const email = form.getValues('email');
        const phone = form.getValues('phone');

        const reCaptchaToken = await props.getRecaptchaTokenFunction();

        try {
          const sessionToken = await initiateAuth({
            email,
            phone,
            method: PinCodeDispatchMethod.SMS,
            reCaptchaPlatform: props.reCaptchaPlatform,
            reCaptchaToken,
          });

          props.onSuccess?.({ sessionToken, email, phone });

          // TODO: Handle ReCaptcha
        } catch (error) {
          props.onError?.(error as Error);
        }
      },
      2000,
      { leading: true, trailing: false }
    ),
    []
  );

  return (
    <View
      style={style.root}
      {...Platform.select({ web: { accessibilityRole: 'form' } })}
    >
      <View>
        <View style={style.faqContainer}>
          <TouchableOpacity
            {...Platform.select({
              web: {
                accessibilityRole: 'link',
                href: FAQ_URL,
                hrefAttrs: {
                  target: '_blank',
                },
              },
              default: {
                onPress() {
                  Linking.openURL(FAQ_URL);
                },
              },
            })}
          >
            <Icon name="help-sms" size="24" />
          </TouchableOpacity>
        </View>
        <Text style={[style.text, style.textTitle, style.textBold]}>
          MyPaidyにログイン・新規登録
        </Text>
        <View style={style.spacer} />
        <View style={style.formField}>
          <TextInput
            placeholder="hello@paidy.com"
            label="メールアドレス"
            keyboardType="email-address"
            textContentType="emailAddress"
            onChangeText={(text) => form.setValue('email', text)}
            value={form.getValues('email')}
            onFocus={() => props.onFocus?.('email')}
            onBlur={() => {
              form.clearError('email');
              form.triggerValidation('email');
            }}
            message={(form.errors.email?.message as string) || ''}
            state={form.errors.email?.message ? 'error' : undefined}
            style={style.formFieldTextInput}
            id="signin-email-field"
          />
        </View>
        <View style={style.formField}>
          <TextInput
            placeholder="09000000000"
            label="携帯電話番号"
            keyboardType="phone-pad"
            textContentType="telephoneNumber"
            onChangeText={(text) => {
              // Allow only digits
              if (text.match(/[^0-9]/) !== null) {
                return;
              }

              form.setValue('phone', text);
            }}
            value={form.getValues('phone')}
            onFocus={() => props.onFocus?.('phone')}
            onBlur={() => {
              form.clearError('phone');
              form.triggerValidation('phone');
            }}
            message={(form.errors.phone?.message as string) || ''}
            state={form.errors.phone?.message ? 'error' : undefined}
            style={style.formFieldTextInput}
            id="signin-phone-field"
          />
        </View>
        {props.customErrorCode !== undefined && (
          <>
            <View style={style.danger}>
              <Text style={style.textCenter}>
                {props.customErrorCode === CustomErrorCode.AUTH_FAIL && (
                  <>
                    <Text style={[style.textDanger, style.textBold]}>
                      入力内容に誤りがあります。
                    </Text>
                  </>
                )}
                {props.customErrorCode === CustomErrorCode.OTP_FAIL && (
                  <Text style={[style.textDanger, style.textBold]}>
                    入力内容に誤りがあります。
                  </Text>
                )}
                {props.customErrorCode === CustomErrorCode.WAF_FAIL && (
                  <Text style={[style.textDanger, style.textBold]}>
                    MyPaidyにアクセスできませんでした。日本国内からのご利用であることを確認してください。
                  </Text>
                )}
              </Text>
            </View>
            <View style={style.spacer} />
          </>
        )}
      </View>
      <View>
        <TouchableOpacity
          style={style.marginBottom}
          {...Platform.select({
            web: {
              accessibilityRole: 'link',
              href: TERMS_URL,
              hrefAttrs: {
                target: '_blank',
              },
            },
            default: {
              onPress() {
                Linking.openURL(TERMS_URL);
              },
            },
          })}
        >
          <Text style={[style.textSecondary, style.textCenter]}>
            <Text style={[style.textSecondary, style.link]}>
              利用規約・個人情報取扱条項
            </Text>
            に同意して
          </Text>
        </TouchableOpacity>
        <Button
          style={style.button}
          title="次へ"
          onPress={validateThenSubmit}
          id="signin-main-button"
        />
      </View>
    </View>
  );
};

const style = StyleSheet.create({
  root: {
    flex: 1,
    justifyContent: 'space-between',
  },
  formField: {
    flexDirection: 'row',
    marginBottom: 24,
  },
  formFieldTextInput: {
    flex: 1,
  },
  text: {
    marginBottom: 8,
  },
  textTitle: {
    fontSize: 20,
  },
  textBold: {
    fontWeight: 'bold',
  },
  textSecondary: {
    fontSize: 12,
  },
  textDanger: {
    color: '#CA4444',
  },
  textCenter: {
    textAlign: 'center',
  },
  spacer: {
    marginBottom: 24,
  },
  faqContainer: {
    alignItems: 'flex-end',
  },
  link: {
    color: '#E52581',
    textDecorationLine: 'underline',
  },
  marginBottom: {
    marginBottom: 16,
  },
  danger: {
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: '#CA4444',
    padding: 16,
    borderRadius: 4,
  },
  button: {
    alignSelf: 'center',
    marginVertical: 8,
  },
});

/**
 * Setup all what is needed for the signin form
 */
const useSignInForm = () => {
  /**
   * react-hook-form requires default values to be set
   */
  const form = useForm<SignInForm>({
    defaultValues: {
      email: '',
      phone: '',
    },
  });

  useEffect(() => {
    form.register(
      {
        name: 'email',
      },
      {
        validate: validator.getRules([
          'mustBeValidEmail',
          'mustNotBeMisspelledDomain',
        ]),
      }
    );

    form.register(
      { name: 'phone' },
      {
        validate: validator.getRules(['mustBeValidPhone']),
      }
    );
  }, []);

  form.watch();

  return form;
};

export default SignIn;
export { CustomErrorCode };
